/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 19th 2023, 8:48:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { TResourceId, policyManager } from "authorization/abac";
import { useGlobalScope } from "core/providers";
import { UpgradeAlert } from "saas-subscription/components/UpgradeAlert";
import { useSubscriptionContext } from "saas-subscription/providers";
import { CircularProgressCentered } from "ui-widgets";

interface IWithAccessCheckArgs {
  resourceId: TResourceId;
  invalidTitle?: React.ReactNode;
  invalidMessage?: React.ReactNode;
}

export const withAccessCheck =
  <P,>(
    args: IWithAccessCheckArgs,
    Component: React.ComponentType<P>,
    AlertWrapperComponent?: React.ComponentType<React.PropsWithChildren<{}>>
  ) =>
  (props: P) => {
    const rule = policyManager.getPolicyRule(args.resourceId);

    if (rule.policy.type === "feature") {
      return withFeatureAccessCheck(args, Component, AlertWrapperComponent)(props);
    }
    return <Component {...props} />;
  };

const withFeatureAccessCheck =
  <P,>(
    { resourceId, invalidTitle, invalidMessage }: IWithAccessCheckArgs,
    Component: React.ComponentType<P>,
    AlertWrapperComponent: React.ComponentType<React.PropsWithChildren<{}>> = ({ children }) => <>{children}</>
  ) =>
  (props: P) => {
    const {
      globalState: { selectedOrganization },
    } = useGlobalScope();
    const { subscriptionTracker, isFetchingSubscriptionData } = useSubscriptionContext();
    const rule = policyManager.getPolicyRule<"feature">(resourceId);
    const canAccess = policyManager.canAccess<"feature">(resourceId, {
      subscriptionCodes: [subscriptionTracker?.product_code],
      id: selectedOrganization.id,
      name: selectedOrganization.name,
      type: "organization",
    });

    return isFetchingSubscriptionData ? (
      <CircularProgressCentered />
    ) : canAccess ? (
      <Component {...props} />
    ) : (
      <AlertWrapperComponent>
        <UpgradeAlert upgradeTo={rule.conditions.subscriptionCodes} title={invalidTitle} message={invalidMessage} />
      </AlertWrapperComponent>
    );
  };
